import type { PropsWithChildren } from "react"
import { useAuth, useConfig, usePermissions } from "@/hooks"
import type { TActivityType, TUser, TVisibility } from "@/types"

interface PermissionsProps extends PropsWithChildren {
  fallback?: () => React.ReactNode
}

export function RoleOnly({ role, children, fallback }: PermissionsProps & { role: TUser["role"] }) {
  const { isOwner, isAdmin, isManager } = usePermissions()

  return role == "user" ||
    (role == "manager" && isManager) ||
    (role == "admin" && isAdmin) ||
    (role == "owner" && isOwner)
    ? children
    : fallback?.()
}

export function SuperOnly({ children }: PermissionsProps) {
  const { isSuper } = usePermissions()
  if (isSuper) return children
}

export function AdminOnly({ children, fallback }: PermissionsProps) {
  const { isAdmin } = usePermissions()
  if (isAdmin) return children
  if (fallback) return fallback()
}

export function SectionAdminOnly({ type, children, fallback }: PermissionsProps & { type: TActivityType }) {
  const { isSectionAdmin } = usePermissions({ activity: type })

  if (isSectionAdmin) return children
  return fallback?.()
}

export function ActivitiesOnly({ type, children, fallback }: PermissionsProps & { type: TActivityType }) {
  const config = useConfig()
  const { isAdmin, isManager } = usePermissions()

  // Section option `meta.edit` defines who can create and start surveys (everyone by default)
  const edit = ((config?.sections?.[type]?.meta?.edit as TVisibility) || "public") as TVisibility
  const canStartSurveys = edit == "public" || isAdmin || (edit == "managers" && isManager)

  if (canStartSurveys) return children
  if (fallback) return fallback()
}

export function ManagerOnly({ teamIds, children, fallback }: PermissionsProps & { teamIds?: string[] }) {
  const { isManager } = usePermissions({ teamIds })
  if (isManager) return children
  if (fallback) return fallback()
}

export function UserOnly({ children, fallback }: PermissionsProps) {
  const { user, company } = useAuth()
  if (user && company) return children
  if (fallback) return fallback()
}
